import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface StreakData {
  streak: number;
  xp: number;
  level: number;
  xpInLevel: number;
  maxXP: number;
  lastActiveDate: string | null;
}

const MAX_XP_PER_LEVEL = 200;

export function useStreaks(): StreakData & { isLoading: boolean; refresh: () => Promise<void> } {
  const { user } = useAuth();
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [lastActiveDate, setLastActiveDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const level = Math.floor(xp / MAX_XP_PER_LEVEL) + 1;
  const xpInLevel = xp % MAX_XP_PER_LEVEL;

  const refresh = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // For MVP: Use client-side storage / profile metadata
      // In production, this would query a dedicated streaks table
      const stored = localStorage.getItem(`streaks_${user.id}`);
      if (stored) {
        const data = JSON.parse(stored);
        setStreak(data.streak || 0);
        setXp(data.xp || 0);
        setLastActiveDate(data.lastActiveDate || null);
      } else {
        // Initialize defaults
        setStreak(0);
        setXp(0);
        setLastActiveDate(null);
      }
    } catch (error) {
      console.error("Error loading streaks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [user?.id]);

  return {
    streak,
    xp,
    level,
    xpInLevel,
    maxXP: MAX_XP_PER_LEVEL,
    lastActiveDate,
    isLoading,
    refresh,
  };
}

export async function completeDailyChallenge(userId: string): Promise<{ streak: number; xp: number }> {
  // Client-side update for MVP
  const stored = localStorage.getItem(`streaks_${userId}`);
  const data = stored ? JSON.parse(stored) : { streak: 0, xp: 0, lastActiveDate: null };

  const today = new Date().toISOString().split("T")[0];
  const lastDate = data.lastActiveDate ? new Date(data.lastActiveDate).toISOString().split("T")[0] : null;
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

  let newStreak = data.streak || 0;
  if (lastDate === today) {
    // Already completed today, no change
  } else if (lastDate === yesterday) {
    // Continuing streak
    newStreak += 1;
  } else {
    // New streak or streak broken
    newStreak = 1;
  }

  const newXp = (data.xp || 0) + 50; // 50 XP per challenge

  const updated = {
    streak: newStreak,
    xp: newXp,
    lastActiveDate: today,
  };

  localStorage.setItem(`streaks_${userId}`, JSON.stringify(updated));

  return { streak: newStreak, xp: newXp };
}

